// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: team threads, the contract (docs/konvo-agents/DESIGN.md, "Team sharing"). The owner of a
// machine shares ONE thread with a teammate at a level: watch (read it live, hear when it
// finishes), comment (pin notes to turns, kept by Konvo; the agent never sees them unless the
// owner sends one) or prompt (prompts go to the agent labelled "<Name> via Konvo"). A prompt share
// can ask for the owner's approval first. The machine itself caps how far its threads may be shared
// (AgentLimits.share, set with `konvo-agents config`): Konvo can share less, never more.

import {z} from 'zod';
import {AgentBrowserLimitSchema, DEFAULT_BROWSER_LIMIT} from './AgentBrowser';
import {AgentThreadSummarySchema} from './Agents';

export const AGENT_SHARE_LEVELS = ['watch', 'comment', 'prompt'] as const;
export type AgentShareLevel = (typeof AGENT_SHARE_LEVELS)[number];
export const AgentShareLevelSchema = z.enum(AGENT_SHARE_LEVELS);

/** What the machine allows: off, or up to a level. */
export const AGENT_MACHINE_SHARING = ['off', 'watch', 'comment', 'prompt'] as const;
export type AgentMachineSharing = (typeof AGENT_MACHINE_SHARING)[number];

const RANK: Record<AgentShareLevel, number> = {watch: 1, comment: 2, prompt: 3};

/** Whether `level` reaches `wanted` (prompt includes comment, comment includes watch). */
export const shareLevelReaches = (level: AgentShareLevel, wanted: AgentShareLevel): boolean =>
	RANK[level] >= RANK[wanted];

/** The level a share really has on a machine: the lower of the two, or null when the machine shares nothing. */
export function effectiveShareLevel(
	level: AgentShareLevel,
	machine: AgentMachineSharing | undefined,
): AgentShareLevel | null {
	if (!machine || machine === 'off') return null;
	return RANK[level] <= RANK[machine] ? level : machine;
}

export const AGENT_SHARE_COMMENT_MAX = 4000;
export const AGENT_SHARE_PEOPLE_MAX = 20;

/** Someone a thread is shared with. Names only: the app draws faces from its own people. */
export const AgentSharePersonSchema = z.object({
	userId: z.string().max(20),
	name: z.string().max(200),
	level: AgentShareLevelSchema,
	/** Prompt level only: their prompts wait for the owner's Approve. */
	approval: z.boolean(),
	sharedAt: z.string().max(40),
});
export type AgentSharePerson = z.infer<typeof AgentSharePersonSchema>;

/** Someone with the thread open right now (renewed every 30 s). */
export const AgentShareHereSchema = z.object({
	userId: z.string().max(20),
	name: z.string().max(200),
	/** 'owner', or the level they have. */
	role: z.union([z.literal('owner'), AgentShareLevelSchema]),
});
export type AgentShareHere = z.infer<typeof AgentShareHereSchema>;

/** A note pinned to one of the agent's replies. Kept by Konvo; the agent never sees it unless the owner sends it. */
export const AgentShareCommentSchema = z.object({
	id: z.string().max(40),
	authorId: z.string().max(20),
	authorName: z.string().max(200),
	text: z.string().max(AGENT_SHARE_COMMENT_MAX),
	/** The reply it is pinned to, and that reply's time (shown as "on the 08:58 reply"). */
	turnId: z.string().max(200),
	turnAt: z.string().max(40),
	/** A reply to another comment. */
	parentId: z.string().max(40).optional(),
	createdAt: z.string().max(40),
	resolvedAt: z.string().max(40).optional(),
	resolvedBy: z.string().max(200).optional(),
	/** When the owner sent it to the agent. */
	sentAt: z.string().max(40).optional(),
});
export type AgentShareComment = z.infer<typeof AgentShareCommentSchema>;

/** A teammate's prompt waiting for the owner (Approval mode). */
export const AgentSharePromptSchema = z.object({
	id: z.string().max(40),
	authorId: z.string().max(20),
	authorName: z.string().max(200),
	text: z.string().max(20_000),
	createdAt: z.string().max(40),
	state: z.enum(['waiting', 'declined']),
});
export type AgentSharePrompt = z.infer<typeof AgentSharePromptSchema>;

/** GET /konvo/agents/machines/:id/threads/:threadId/share: everything about a thread's sharing, for its owner or a teammate on it. */
export const AgentThreadShareSchema = z.object({
	role: z.enum(['owner', 'teammate']),
	owner: z.object({userId: z.string().max(20), name: z.string().max(200)}),
	machine: z.object({
		id: z.string().max(20),
		name: z.string().max(200),
		online: z.boolean(),
		lastSeenAt: z.string().nullable(),
		/** How far the machine's threads may be shared: the owner's setting in Konvo. */
		sharing: z.enum(AGENT_MACHINE_SHARING),
		/** The machine's own limits, which bound everyone. */
		send: z.boolean(),
		approve: z.boolean(),
		/** Its bridge can dismiss an agent's question (konvo-agents 0.36.0+). */
		dismissQuestion: z.boolean().optional(),
	}),
	thread: z.object({id: z.string().max(200), title: z.string().max(500), project: z.string().max(200).optional()}),
	/** A teammate's own share: their level after the machine's cap, and whether their prompts wait for approval. */
	me: z.object({level: AgentShareLevelSchema, approval: z.boolean()}).optional(),
	people: z.array(AgentSharePersonSchema).max(AGENT_SHARE_PEOPLE_MAX),
	here: z.array(AgentShareHereSchema).max(AGENT_SHARE_PEOPLE_MAX + 1),
	comments: z.array(AgentShareCommentSchema).max(500),
	/** Waiting prompts: all of them for the owner, a teammate's own. */
	prompts: z.array(AgentSharePromptSchema).max(100),
});
export type AgentThreadShare = z.infer<typeof AgentThreadShareSchema>;

/** One thread shared with the caller, for "Shared with you" (desktop and phone). */
export const AgentSharedThreadSchema = z.object({
	machineId: z.string().max(20),
	threadId: z.string().max(200),
	owner: z.object({userId: z.string().max(20), name: z.string().max(200)}),
	machineName: z.string().max(200),
	/** The agent app on the machine, e.g. "T3 Code". */
	tool: z.string().max(80).optional(),
	online: z.boolean(),
	lastSeenAt: z.string().nullable(),
	level: AgentShareLevelSchema,
	approval: z.boolean(),
	title: z.string().max(500),
	project: z.string().max(200).optional(),
	/** The thread as the machine last listed it (status, needs you, times). */
	summary: AgentThreadSummarySchema.optional(),
});
export type AgentSharedThread = z.infer<typeof AgentSharedThreadSchema>;

/** GET /konvo/agents/shared */
export const AgentSharedWithMeSchema = z.object({threads: z.array(AgentSharedThreadSchema).max(200)});
export type AgentSharedWithMe = z.infer<typeof AgentSharedWithMeSchema>;

/** GET /konvo/agents/shares: what the owner shares now, per machine (Settings → Connections → Agents). */
export const AgentOwnerSharesSchema = z.object({
	machines: z.array(
		z.object({
			machineId: z.string().max(20),
			/** The owner's team sharing setting for the machine. */
			sharing: z.enum(AGENT_MACHINE_SHARING),
			threads: z
				.array(
					z.object({
						threadId: z.string().max(200),
						title: z.string().max(500),
						people: z.array(AgentSharePersonSchema).max(AGENT_SHARE_PEOPLE_MAX),
					}),
				)
				.max(200),
		}),
	),
});
export type AgentOwnerShares = z.infer<typeof AgentOwnerSharesSchema>;

/** PUT .../share/people/:userId */
export const AgentShareSetSchema = z.object({level: AgentShareLevelSchema, approval: z.boolean().optional()});
export type AgentShareSet = z.infer<typeof AgentShareSetSchema>;

/**
 * PATCH /konvo/agents/machines/:id/settings: the owner's settings for a machine, kept by Konvo (not
 * on the machine): how far its threads may be shared, and whether Allow / Deny from Konvo may answer
 * its approval requests (for the owner and teammates alike, in the app and on the lock screen).
 */
export const AgentMachineSettingsSchema = z.object({
	share: z.enum(AGENT_MACHINE_SHARING),
	approve: z.boolean(),
	/** What the Agents browser may reach through the machine (AgentBrowser.ts). */
	browser: AgentBrowserLimitSchema,
	/** Terminals from Konvo: open the machine's terminals, see them live and type into them (owner only). On by default. */
	terminals: z.boolean(),
});
export type AgentMachineSettings = z.infer<typeof AgentMachineSettingsSchema>;
export const AgentMachineSettingsPatchSchema = AgentMachineSettingsSchema.partial().refine(
	(patch) =>
		patch.share !== undefined ||
		patch.approve !== undefined ||
		patch.browser !== undefined ||
		patch.terminals !== undefined,
	{message: 'Change team sharing, Allow and Deny, the browser or terminals.'},
);

/** A machine Konvo first sees: sharing up to prompt (it just works), Allow / Deny off. */
export const DEFAULT_MACHINE_SETTINGS: AgentMachineSettings = {
	share: 'prompt',
	approve: false,
	browser: DEFAULT_BROWSER_LIMIT,
	terminals: true,
};

/** POST .../share/comments */
export const AgentShareCommentRequestSchema = z.object({
	text: z.string().trim().min(1).max(AGENT_SHARE_COMMENT_MAX),
	turnId: z.string().min(1).max(200),
	turnAt: z.string().min(1).max(40),
	parentId: z.string().max(40).optional(),
});
export type AgentShareCommentRequest = z.infer<typeof AgentShareCommentRequestSchema>;

/** POST .../share/prompts */
export const AgentSharePromptRequestSchema = z.object({prompt: z.string().trim().min(1).max(20_000)});

/** POST .../share/prompts/:promptId */
export const AgentSharePromptDecisionSchema = z.object({decision: z.enum(['approve', 'decline'])});

/** Error codes of team sharing (HTTP 403 unless noted). */
export const AGENT_SHARE_ERROR_CODES = {
	/** The share's level (or the machine's cap, or its own limits) doesn't allow that. */
	notAllowed: 'SHARE_NOT_ALLOWED',
	/** Approval mode: prompts go to the owner first (POST .../share/prompts). 409. */
	approvalMode: 'SHARE_APPROVAL_MODE',
	/** The machine doesn't allow team sharing (or not that far). 409. */
	sharingOff: 'SHARING_OFF',
	/** You can't share with that person (yourself, a bot, someone without Agents). 400. */
	badPerson: 'SHARE_BAD_PERSON',
	/** No share of this thread for the caller (removed, sharing switched off, the thread gone). 404. */
	ended: 'SHARE_ENDED',
} as const;
